import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPatients } from "../../store/slices/patientsSlice";
import Select from "react-select";

const CaseManagementForm = ({ formData, setFormData, errors, readOnly = false }) => {
  const dispatch = useDispatch();
  const { patients } = useSelector((state) => state.patients);
  const [showOtherText, setShowOtherText] = useState(false);

  useEffect(() => {
    dispatch(fetchPatients());
  }, [dispatch]);

  useEffect(() => {
    // Initialize occupational_status if it's not defined
    if (!formData.occupational_status) {
      setFormData((prev) => ({ ...prev, occupational_status: [] }));
    } else {
      setShowOtherText(formData.occupational_status.includes("others"));
    }
  }, [formData.occupational_status, setFormData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (option) => {
    if (readOnly) return;
    setFormData((prev) => {
      const current = prev.occupational_status || [];
      let updated;
      if (current.includes(option)) {
        updated = current.filter((item) => item !== option);
        if (option === "others") {
          setShowOtherText(false);
          return { ...prev, occupational_status: updated, occupational_status_other: "" };
        }
      } else {
        updated = [...current, option];
        if (option === "others") {
          setShowOtherText(true);
        }
      }
      return { ...prev, occupational_status: updated };
    });
  };

  const occupationalOptions = [
    "Working",
    "Studying",
    "Looking for a Job",
    "Never Employed",
    "Inducted in Family Business",
    "Managing Household",
    "others",
  ];

  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '38px',
      fontSize: '0.875rem',
      borderRadius: '0.375rem',
      borderColor: errors.patient_id ? '#ff3e1d' : '#d9dee3',
      '&:hover': { borderColor: errors.patient_id ? '#ff3e1d' : '#d9dee3' }
    }),
    menu: (base) => ({ ...base, fontSize: '0.875rem', zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#666ee8' : state.isFocused ? '#f5f5f9' : 'white',
      color: state.isSelected ? 'white' : '#566a7f',
      cursor: 'pointer',
    })
  };

  const patientOptions = (patients || []).map((p) => ({
    value: p.id,
    label: `${p.first_name || ""} ${p.last_name || ""} (${p.mr_no || "N/A"})`.trim(),
  }));

  const callTypeOptions = [
    { value: "well being", label: "Well Being" },
    { value: "follow up", label: "Follow Up" }
  ];

  const interactionTypeOptions = [
    { value: "PTCl", label: "PTCL" },
    { value: "message WhatsApp", label: "WhatsApp Message" }
  ];

  const serviceTypeOptions = [
    { value: "OPD", label: "OPD" },
    { value: "Home Visit", label: "Home Visit" },
    { value: "PRP", label: "PRP" }
  ];

  const currentPatient = patientOptions.find(opt => opt.value === parseInt(formData.patient_id)) || null;
  const currentCallType = callTypeOptions.find(opt => opt.value === formData.call_type) || null;
  const currentInteractionType = interactionTypeOptions.find(opt => opt.value === formData.interaction_type) || null;
  const currentServiceType = serviceTypeOptions.find(opt => opt.value === formData.service_type) || null;

  return (
    <div className="card-body">
      <div className="row g-4">
        {/* ========================================== */}
        {/* SECTION 1: Client & Interaction Details   */}
        {/* ========================================== */}
        <div className="col-12">
          <h6 className="fw-semibold text-primary border-bottom pb-2">
            <i className="ri-user-voice-line me-1"></i> Client & Interaction Details
          </h6>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label text-secondary small">Client Name <span className="text-danger">*</span></label>
              <Select
                options={patientOptions}
                styles={selectStyles}
                value={currentPatient}
                onChange={(opt) => setFormData(prev => ({ ...prev, patient_id: opt ? opt.value : "" }))}
                placeholder="Search Patient"
                isSearchable
                isDisabled={readOnly || !!formData.patient_id_fixed}
              />
              {errors.patient_id && <div className="text-danger small mt-1">{errors.patient_id[0] || errors.patient_id}</div>}
            </div>

            <div className="col-md-6">
              <label className="form-label text-secondary small">Case Manager</label>
              <input
                type="text"
                name="case_manager"
                className="form-control"
                placeholder="Enter Case Manager Name"
                value={formData.case_manager || ""}
                onChange={handleChange}
                disabled={readOnly}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label text-secondary small">Call Type</label>
              <Select
                options={callTypeOptions}
                styles={selectStyles}
                value={currentCallType}
                onChange={(opt) => setFormData(prev => ({ ...prev, call_type: opt ? opt.value : "" }))}
                placeholder="Select Call Type"
                isSearchable
                isDisabled={readOnly}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label text-secondary small">Interaction Type</label>
              <Select
                options={interactionTypeOptions}
                styles={selectStyles}
                value={currentInteractionType}
                onChange={(opt) => setFormData(prev => ({ ...prev, interaction_type: opt ? opt.value : "" }))}
                placeholder="Select Interaction Type"
                isSearchable
                isDisabled={readOnly}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label text-secondary small">Calling Date</label>
              <input
                type="date"
                name="calling_date"
                className="form-control"
                value={formData.calling_date || ""}
                onChange={handleChange}
                disabled={readOnly}
              />
            </div>

            <div className="col-12">
              <label className="form-label text-secondary small">Remarks</label>
              <textarea
                name="remarks"
                rows="3"
                className="form-control"
                placeholder="Enter remarks..."
                value={formData.remarks || ""}
                onChange={handleChange}
                disabled={readOnly}
              ></textarea>
            </div>
          </div>
        </div>

        {/* ========================================== */}
        {/* SECTION 2: Employment / Occupational Status */}
        {/* ========================================== */}
        <div className="col-12 mt-4">
          <h6 className="fw-semibold text-primary border-bottom pb-2">
            <i className="ri-briefcase-line me-1"></i> Employment / Occupational Status
          </h6>
          <div className="d-flex flex-wrap gap-3 py-2">
            {occupationalOptions.map((opt) => (
              <div className="form-check" key={opt}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`occ_${opt}`}
                  checked={(formData.occupational_status || []).includes(opt)}
                  onChange={() => handleCheckboxChange(opt)}
                  disabled={readOnly}
                />
                <label className="form-check-label text-dark" htmlFor={`occ_${opt}`}>
                  {opt === "others" ? "Others" : opt}
                </label>
              </div>
            ))}
          </div>

          {showOtherText && (
            <div className="col-md-6 mt-2">
              <label className="form-label text-secondary small">Please Specify</label>
              <input
                type="text"
                name="occupational_status_other"
                className="form-control"
                placeholder="Specify other status"
                value={formData.occupational_status_other || ""}
                onChange={handleChange}
                disabled={readOnly}
              />
            </div>
          )}
        </div>

        {/* ========================================== */}
        {/* SECTION 3: Review Schedule                 */}
        {/* ========================================== */}
        <div className="col-12 mt-4">
          <h6 className="fw-semibold text-primary border-bottom pb-2">
            <i className="ri-calendar-todo-line me-1"></i> Review Schedule
          </h6>
          <div className="col-12">
            <label className="form-label text-secondary small">Review Frequency</label>
            <textarea
              name="review_frequency"
              rows="2"
              className="form-control"
              placeholder="e.g. Weekly, Bi-weekly, Monthly..."
              value={formData.review_frequency || ""}
              onChange={handleChange}
              disabled={readOnly}
            ></textarea>
          </div>
        </div>

        {/* ========================================== */}
        {/* SECTION 4: Service Schedules               */}
        {/* ========================================== */}
        <div className="col-12 mt-4 mb-3">
          <h6 className="fw-semibold text-primary border-bottom pb-2">
            <i className="ri-customer-service-line me-1"></i> Service Schedules
          </h6>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label text-secondary small">Select Service</label>
              <Select
                options={serviceTypeOptions}
                styles={selectStyles}
                value={currentServiceType}
                onChange={(opt) => setFormData(prev => ({ ...prev, service_type: opt ? opt.value : "" }))}
                placeholder="Select Service"
                isSearchable
                isDisabled={readOnly}
              />
            </div>

            {formData.service_type && (
              <>
                <div className="col-md-4">
                  <label className="form-label text-secondary small">Service Schedule Date</label>
                  <input
                    type="date"
                    name="service_schedule_date"
                    className="form-control"
                    value={formData.service_schedule_date || ""}
                    onChange={handleChange}
                    disabled={readOnly}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label text-secondary small">Service Frequency</label>
                  <input
                    type="text"
                    name="service_frequency"
                    className="form-control"
                    placeholder="e.g. Once a week, Monthly..."
                    value={formData.service_frequency || ""}
                    onChange={handleChange}
                    disabled={readOnly}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseManagementForm;
